import { Subject } from 'rxjs';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { platform } from '../../../platform/platform';
import { ApplicationError } from '../../_errors/application-error';
import { Mark, MarkItem, MarkUpsert } from '../../_objects/mark';
import { Title, TitleItem, TitleUpsert } from '../../_objects/title';
import { UtilsService } from '../../_services/_utils.service';
import { CommonSnackBarService } from '../../_services/common-snack-bar.service';
import { LoginUserOnlineService } from '../../_services/http-api/login-user-online.service';
import { MarkService } from '../../_services/http-api/mark.service';
import { TenantConfigService } from '../../_services/http-api/tenant-config.service';
import { TitleService } from '../../_services/http-api/title.service';
import { LangUpdateService } from '../../_services/lang-update.service';
import { NetworkStatusService } from '../../_services/network-status.service';
import { SpinnerService } from '../../_services/spinner.service';
import { MARK_COLOR_ITEMS, MARK_DISABLED_COLOR, MARKCOLORITEMS, PLATFORM, REGULAR_EXPRESSION, TenantConfig } from '../../app.constants';
import { CommonSaveConfirmDialogComponent } from '../../common-dialog/common-save-confirm-dialog.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonDeleteDialogComponent } from '../../common-dialog/common-delete-dialog.component';
import { CommonLayoutService } from '../../common-layout/common-layout.service';
import { takeUntil } from 'rxjs/operators';
import { MarksStateService } from "../../_services/mark-state/marks-state.service";
import { TitlesStateService } from "../../_services/title-state/titles-state.service";
import { CallToActionLabel } from '../../shared/directives/cta/cta.directive';
import { IconEnum } from '@evc/web-components';
import { Call } from "@angular/compiler";
import { Router } from '@angular/router';
import { NavigationMenuService } from '../../shared/services/navigation-menu/navigation-menu.service';

@Component({
    selector: 'app-settings-marks-edit-dialog',
    templateUrl: './settings-marks-edit-dialog.component.html',
    styleUrls: ['./settings-marks-edit-dialog.component.scss'],
    standalone: false
})
export class SettingsMarksEditDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  displayedMarkColumns = ['mark', 'label', 'color', 'warning'];
  markListDataSource = new MatTableDataSource<MarkItem>();
  marks: Mark;
  addMarks: MarkItem[] = [];
  preMarks: MarkItem[];
  markItems: MARKCOLORITEMS[];
  isTrans = false;
  isAddMarkDisabled = false;
  availableSpots: number[] = [];

  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;
  private onDestroy$ = new Subject();

  readonly minMarkLength = 0;
  readonly maxAddMarkLength = 7;
  readonly maxDefaultMarkLength = 1;
  readonly markCodeMap = new Map([[0, 'X'], [1, 'A'], [2, 'B'], [3, 'C'], [4, 'D'], [5, 'E'], [6, 'F'], [7, 'G']]);
  readonly idPrefix = 'label_';
  readonly disabledColorValue = MARK_DISABLED_COLOR;
  isOnPremise = true;
  onlineOnlyMarks = ['E', 'F', 'G'];
  public isLeftbarOpen: boolean = false;

  constructor(
    private markService: MarksStateService,
    private snackBar: CommonSnackBarService,
    private tenantConfigService: TenantConfigService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private utilsService: UtilsService,
    private commonLayoutService: CommonLayoutService,
    private cd: ChangeDetectorRef,
    private networkStatusService: NetworkStatusService,
    private router: Router,
    private navigationMenuService: NavigationMenuService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.markItems = this.getSelectColorItems();
    this.isOnPremise = this.networkStatusService.isOnPremise;
    this.loadMark();
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.isCheckChange()) {
      return true;
    }

    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
      width: '432px',
      disableClose: true,
      data: {
        isMove: true,
        isError: this.isValidationError,
        message: 'settingsMarkTitle.message',
        customMessage: 'settingsMarkTitle.customMessage',
        notSaveBtnLabel: 'settingsMarkTitle.notSaveBtnLabel',
      }
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result === false) {
      dialogRef.close();
      this.resetEmptyColors();
      return true;
    } else if (result === '') {
      dialogRef.close();
      this.router.navigate([this.router.url], { skipLocationChange: true });
      return false;
    } else {
      this.submit();
      this.resetEmptyColors();
      return true;
    }
  }

  get isDisabledNewButton(): boolean {
    return this.isTrans || this.addMarks.length >= this.maxAddMarkLength || this.isValidationError;
  }

  shouldShowSettingsMenu () {
    return (this.router.url.includes('/mark') ||
       this.router.url.includes('/title') ||
       this.router.url.includes('/tags') ||
       this.router.url.includes('/tenant-config'));
  }

  ngAfterViewInit() {
    this.navigationMenuService.leftBarState$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(isLeftbarOpen => {
        this.isLeftbarOpen = isLeftbarOpen;
      });
  }

  ngOnInit() {
    this.setConfig();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.markLabel = configs.find(_ => _.key === TenantConfig.LabelOfMark.KEY).value as string;
    }
  }

  getSelectColorItems(): MARKCOLORITEMS[] {
    return MARK_COLOR_ITEMS;
  }

  getColorNameByTagColor(colorTag: string): string {
    return this.markItems.find(colorItem => colorItem.color === colorTag)?.name ?? '';
  }

  isColorAvailable(selectedColor: MARKCOLORITEMS): boolean {
    return !this.markListDataSource.data.some(({ color }) => selectedColor.color === color);
  }

  getMarkPageTitle(): string {
    return `${this.markLabel}`;
  }

  loadMark() {
    this.oscSpinnerService.show(true);
    this.markService.mark$.subscribe(
      data => {
        this.markListDataSource.data = this.sortMarksByCode(data.items);
        this.addMarks = data.items.filter(item => item.identifier >= this.maxDefaultMarkLength
          && item.identifier < this.maxDefaultMarkLength + this.maxAddMarkLength);
        this.marks = data;
        this.isAddMarkDisabled = this.addMarks.length >= this.maxAddMarkLength;
        this.preMarks = JSON.parse(JSON.stringify(data.items));
        this.availableSpots = this.calculateAvailableSpots(data.items);
        this.completeMarks();
        this.commonLayoutService.commonLayoutNewMarkEmit(this.isDisabledNewButton);
        this.oscSpinnerService.hide(true);
      },
      error => {
        console.log(error);
        this.oscSpinnerService.hide(true);
      }
    );
  }

  resetEmptyColors() {
    this.markListDataSource.data.forEach(mark => {
      mark.color = mark.color === '' ? this.disabledColorValue : mark.color;
    });
  }

  completeMarks() {
    const totalMarks = this.markListDataSource.data.length;
    for (let i = totalMarks; i < this.maxAddMarkLength + this.maxDefaultMarkLength; i++) {
      this.addMarks.push({
        identifier: this.availableSpots.shift() ?? this.getNewMarkIdentifier(),
        code: this.markCodeMap.get(i) ?? '',
        label: '',
        color: this.disabledColorValue
      });
    }

    if (this.isOnPremise) {
      this.addMarks.forEach(mark => {
        if (this.onlineOnlyMarks.includes(mark.code)) {
          mark.color = this.disabledColorValue;
        }
      });
    }

    this.setAdditionalMark();
  }

  calculateAvailableSpots(items: MarkItem[]): number[] {
    const usedIdentifiers = items.map(item => item.identifier);
    const availableSpots = [];
    for (let i = this.maxDefaultMarkLength; i < this.maxDefaultMarkLength + this.maxAddMarkLength; i++) {
      if (!usedIdentifiers.includes(i)) {
        availableSpots.push(i);
      }
    }
    return availableSpots;
  }

  addMark() {
    const addMark = new MarkItem();

    addMark.identifier = this.availableSpots.shift() ?? this.getNewMarkIdentifier();
    addMark.code = this.markCodeMap.get(addMark.identifier);
    addMark.label = '';
    addMark.color = this.disabledColorValue;
    this.addMarks.push(addMark);

    this.setAdditionalMark();
    this.cd.detectChanges();

    const id = this.idPrefix + addMark.identifier;
    const element = document.getElementById(id);
    element?.focus();
  }

  getNewMarkIdentifier(): number {
    const usedIdentifiers = this.addMarks.map(mark => mark.identifier);
    for (let i = this.maxDefaultMarkLength; i < this.maxAddMarkLength + this.maxDefaultMarkLength; i++) {
      if (!usedIdentifiers.includes(i)) {
        return i;
      }
    }
    return this.maxAddMarkLength + this.maxDefaultMarkLength;
  }

  setAdditionalMark() {
    const markABCD = this.markListDataSource.data
      .filter(item => item.identifier > this.minMarkLength && item.identifier < this.maxDefaultMarkLength);
    const markX = this.markListDataSource.data.filter(item => item.identifier === this.minMarkLength);
    this.markListDataSource.data = this.sortMarksByCode(markABCD.concat(this.addMarks, markX));
  }

  toggleMark(identifier: number) {
    const mark = this.addMarks.find(item => item.identifier === identifier);

    if (!mark || this.isOnPremise && this.onlineOnlyMarks.includes(mark.code)) {
      return;
    }

    mark.color = (mark.color === this.disabledColorValue) ? '' : this.disabledColorValue;

    this.cd.detectChanges();
    if (mark.color !== '') {
      this.submit();
    }
  }

  isMarkDisabled(color: string): boolean {
    return color === this.disabledColorValue;
  }

  isRegularTextPattern(label: string): boolean {
    if (!label) {
      throw new ApplicationError('mark is null.');
    }

    const valid = this.utilsService.CombiningRegExp([
      '^[',
      REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
      REGULAR_EXPRESSION.KANJI_PATTERN,
      REGULAR_EXPRESSION.SYMBOL_PATTERN,
      REGULAR_EXPRESSION.OTHER_SYMBOL_PATTERN,
      ']+$'
    ]);

    return !label.match(valid);
  }

  isCheckChange(): boolean {
    return JSON.stringify(this.getUsualData()) !== JSON.stringify(this.preMarks);
  }

  get isValidationError(): boolean {
    return this.getUsualData().some(item => {
      const isDisabled = this.isMarkDisabled(item.color);
      return isDisabled ? false : (!isDisabled && (item.label.trim().length > 25 || item.label.trim().length < 1 || item.color.length < 1
        || this.isRegularTextPattern(item.label)
        || !['X', 'A', 'B', 'C', 'D', 'E', 'F', 'G'].includes(item.code)));
    });
  }

  submit() {
    this.commonLayoutService.commonLayoutNewMarkEmit(this.isDisabledNewButton);

    if (!this.isCheckChange()) {
      return;
    }
    if (this.isValidationError) {
      this.snackBar.open(this.translate.instant('settingsMarkTitle.submitInvalid', { markLabel: this.markLabel }), null, null, 5000);
      return;
    }

    this.isTrans = true;
    this.oscSpinnerService.show(true);
    const markUpsert: MarkUpsert = {
      items: this.getUsualData(),
      schemaVersion: this.marks.schemaVersion,
    };

    this.markService.upsertMark(markUpsert).subscribe(
      mark => {
        this.isTrans = false;
        this.snackBar.open(this.translate.instant('settingsMarkTitle.submitSuccessfully', { markLabel: this.markLabel }), null, null, 5000);
        this.marks = mark;
        this.oscSpinnerService.hide(true);
      },
      error => {
        this.isTrans = false;
        this.snackBar.open(this.translate.instant('settingsMarkTitle.submitError', { markLabel: this.markLabel }), null, null, 5000);
        this.oscSpinnerService.hide(true);
        console.log(error);
      }
    );
  }

  requiredLabel(label: string) {
    return !label || label.trim().length < 1;
  }

  requiredColor(color: string) {
    return !color || color.trim().length < 1;
  }

  getUsualData() {
    return this.markListDataSource.data.filter(item => item.identifier >= this.minMarkLength);
  }

  sortMarksByCode(items: MarkItem[]): MarkItem[] {
    return items.sort((a, b) => {
      if (a.code === 'X') {
        return -1;
      } else if (b.code === 'X') {
        return 1;
      } else {
        return (a.code > b.code) ? 1 : ((b.code > a.code) ? -1 : 0);
      }
    });
  }
}

@Component({
    selector: 'app-settings-titles-edit-dialog',
    templateUrl: './settings-titles-edit-dialog.component.html',
    styleUrls: ['./settings-titles-edit-dialog.component.scss'],
    standalone: false
})

export class SettingsTitlesEditDialogComponent implements OnInit, OnDestroy {
  callToActionLabel: CallToActionLabel = {
    label: this.translate.instant('commonLayout.newTitle'),
    icon: IconEnum.Plus
  };
  readonly idPrefix = 'title_';
  @ViewChild('table') table: MatTable<TitleItem>;
  displayedTitleColumns = ['title'];
  titleListDataSource = new MatTableDataSource<TitleItem>();
  titles: Title;
  preTitles: TitleItem[];
  selectedTitle: TitleItem;

  selectedIndex = 0;

  isTrans = false;
  isError: boolean;

  titleLabel = TenantConfig.DefaultConfigurationValues.LabelOfTitle;
  private onDestroy$ = new Subject();
  private readonly maxTitleCount = 50;

  get lowerCaseTitleLabel(): string {
    return this.titleLabel.toLowerCase();
  }

  constructor(
    private titleService: TitlesStateService,
    private snackBar: CommonSnackBarService,
    private tenantConfigService: TenantConfigService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private utilsService: UtilsService,
    private commonLayoutService: CommonLayoutService,
    private cd: ChangeDetectorRef,
    private elementRef: ElementRef,
    private router: Router
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.loadTitle();
  }

  get isDisabledNewButton(): boolean {
    return !(this.titleListDataSource.data.length < this.maxTitleCount
      && !this.titleListDataSource.data.some(item => !item.identifier));
  }

  getTitlesPageTitle(): string {

    return `${this.titleLabel} (${this.preTitles.length}/${this.maxTitleCount})`;
  }

  getFormattedTitleIndex(index: number) {
    return index < 9 ? `0${index + 1}` : `${index + 1}`;
  }

  shouldShowActionButton() {
    return this.router.url.includes('/title');
  }

  ngOnInit() {
    this.setTitleSubscribe();
    this.setConfig();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  setTitleSubscribe() {
    this.commonLayoutService
      .commonLayoutNewTitleObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(isDisabled => {
        if (isDisabled === null) {
          this.addTitle();
        }
      });
  }

  async canDeactivate(): Promise<boolean> {

    if (!this.isCheckChange()) {
      return true;
    }

    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
      width: '432px',
      disableClose: true,
      data: {
        isMove: true,
        isError: this.isValidationError,
        message: 'settingsMarkTitle.message',
        customMessage: 'settingTenant.customMessage',
        notSaveBtnLabel: 'settingsMarkTitle.notSaveBtnLabel',
      }
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result === false) {
      dialogRef.close();
      return true;
    } else if (result === '') {
      dialogRef.close();
      return false;
    } else {
      this.submit();
      return true;
    }
  }

  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.titleLabel = configs.find(_ => _.key === TenantConfig.LabelOfTitle.KEY).value as string;
    }
  }

  loadTitle() {
    this.oscSpinnerService.show(true);
    this.titleService.titles$
      .subscribe(item => {
        this.titleListDataSource.data = item.items;
        this.titles = item;
        this.preTitles = JSON.parse(JSON.stringify(item.items));
        this.commonLayoutService.commonLayoutNewTitleEmit(this.isDisabledNewButton);
        this.oscSpinnerService.hide(true);
      });
  }

  selectTitle(identifier: TitleItem, index: number): TitleItem {
    this.selectedIndex = index;
    return this.selectedTitle = identifier;
  }

  addTitle() {
    if (this.titleListDataSource.data.length < 50) {
      const tempTitle = this.titleListDataSource.data;
      const emptyTitle: TitleItem = { identifier: '' };
      tempTitle.push(emptyTitle);
      this.titleListDataSource.data = tempTitle;
      this.commonLayoutService.commonLayoutNewTitleEmit(this.isDisabledNewButton);
      this.cd.detectChanges();

      this.elementRef.nativeElement.scrollIntoView({ behavior: 'instant', block: 'end' });
      const id = this.idPrefix + (this.titleListDataSource.data.length -1);
      const element = document.getElementById(id);
      element?.focus();
    }
  }

  isDisableAddButton(): boolean {
    return !(this.titleListDataSource.data.length <= 49 && !this.titleListDataSource.data.some(item => !item.identifier));
  }

  deleteTitle(index) {
    const tempTitle = this.titleListDataSource.data;
    const removedElement = tempTitle[index];
    if (!removedElement.identifier) {
      tempTitle.splice(index, 1)[0];
      this.titleListDataSource.data = tempTitle;
      this.selectedTitle = null;
      this.submit();
      this.commonLayoutService.commonLayoutNewTitleEmit(this.isDisabledNewButton);
    } else {
      const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
        width: '600px',
        minWidth: '400px',
        maxHeight: '800px',
        data: { target: this.translate.instant('settingsTitlesEditDialog.deleteDialogTarget', { titleLabel: this.titleLabel }), deleteNames: [removedElement.identifier] }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          const tempTitle = this.titleListDataSource.data;
          tempTitle.splice(index, 1)[0];
          this.titleListDataSource.data = tempTitle;
          this.selectedTitle = null;
          this.submit();
          this.commonLayoutService.commonLayoutNewTitleEmit(this.isDisabledNewButton);
        }
      });
    }
  }

  isDisableDeleteButton(index: number) {
    const tempTitle = this.titleListDataSource.data;
    const targetTitle = tempTitle.filter((_, i) => i !== index);
    if (!targetTitle) {
      return false;
    } else if (targetTitle.length === 0) {
      return true;
    } else {
      return targetTitle.some(title => title.identifier === null || title.identifier === undefined || title.identifier === "");
    }
  }

  isRegularTextPattern(label: string): boolean {
    if (label === null || label === undefined) {
      throw new ApplicationError('title is null.');
    }

    const valid = this.utilsService.CombiningRegExp([
      '^[',
      REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
      REGULAR_EXPRESSION.KANJI_PATTERN,
      REGULAR_EXPRESSION.SYMBOL_PATTERN,
      REGULAR_EXPRESSION.OTHER_SYMBOL_PATTERN,
      ']+$'
    ]);

    return !label.match(valid);
  }

  isCheckChange(): boolean {
    const current = JSON.stringify(this.titleListDataSource.data);
    const previous = JSON.stringify(this.preTitles);
    return current !== previous;
  }

  isValidationError(): boolean {
    if (this.titleListDataSource.data.length > 50 || this.titleListDataSource.data.length < 1) {
      return true;
    }
    return this.titleListDataSource.data.some(item => {
      if (item.identifier.trim().length > 30 || item.identifier.trim().length < 1
        || item.identifier === '') {
        return true;
      }
      if (this.isRegularTextPattern(item.identifier)) {
        return true;
      }
    });
  }

  isSelectedTitle(titleItem: TitleItem, index: number) {
    if (!this.selectedTitle || !this.selectedTitle.identifier || !titleItem) {
      return false;
    }
    return (this.selectedTitle.identifier === titleItem.identifier && this.selectedIndex === index);
  }

  dropTable(event: CdkDragDrop<TitleItem[]>) {
    const prevIndex = this.titleListDataSource.data.findIndex((d) => d === event.item.data);
    moveItemInArray(this.titleListDataSource.data, prevIndex, event.currentIndex);
    this.table.renderRows();
    this.submit();
  }

  submit() {
    this.commonLayoutService.commonLayoutNewTitleEmit(this.isDisabledNewButton);
    if (!this.isCheckChange() || this.isValidationError()) {
      return;
    }
    this.isTrans = true;
    this.oscSpinnerService.show(true);
    if (!this.isValidationError()) {
      const titleUpsert: TitleUpsert = new TitleUpsert();
      titleUpsert.items = this.titleListDataSource.data;
      titleUpsert.schemaVersion = this.titles.schemaVersion;

      const onError = (error: unknown) => {
        this.isTrans = false;
        this.snackBar.open(this.translate.instant('settingsMarkTitle.submitError', { markLabel: this.titleLabel }), null, null, 5000);
        this.oscSpinnerService.hide(true);
        console.log(error);
      };

      const onSuccess = (title: Title) => {
        this.isTrans = false;
        this.snackBar.open(this.translate.instant('settingsMarkTitle.submitSuccessfully', { markLabel: this.titleLabel }), null, null, 5000);
        this.titles = title;
        this.oscSpinnerService.hide(true);
      };

      this.titleService.upsertTitle(titleUpsert)
        .subscribe(
          data => onSuccess(data),
          error => onError(error));
    } else {
      this.isTrans = false;
      this.oscSpinnerService.hide(true);
      this.snackBar.open(this.translate.instant('settingsTitlesEditDialog.submitInvalid', { titleLabel: this.titleLabel }));
    }
  }

  /**
   * identifierに値が入力されているか必須確認を行う
   */
  requiredIdentifier(identifier: string) {
    return !identifier || identifier.trim().length < 1;
  }

  protected readonly Call = Call;
}

@Component({
    selector: 'app-settings-mark-title',
    templateUrl: './settings-mark-title.component.html',
    styleUrls: ['./settings-mark-title.component.scss'],
    standalone: false
})
export class SettingsMarkTitleComponent implements OnInit, OnDestroy {
  browserName: string;
  isAdmin = false;

  /** オフラインかどうか。 */
  public get isOffline(): boolean {
    return this.networkStatusService.isOffline;
  }

  markListDataSource = new MatTableDataSource<MarkItem>();
  titleListDataSource = new MatTableDataSource<TitleItem>();
  displayedMarkColumns = ['mark', 'label', 'color', 'warning'];
  displayedTitleColumns = ['title'];

  isTrans = false;

  // ラベル
  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;
  titleLabel = TenantConfig.DefaultConfigurationValues.LabelOfTitle;
  private onDestroy$ = new Subject();

  constructor(
    private markService: MarkService,
    private titleService: TitleService,
    private tenantConfigService: TenantConfigService,
    private utilsService: UtilsService,
    private loginUserService: LoginUserOnlineService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private networkStatusService: NetworkStatusService,
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnInit() {
    this.browserName = this.utilsService.getBrowserName();
    this.isAdmin = this.loginUserService.isAdminRole();
    if (this.isAdmin || this.isOffline) {
      this.oscSpinnerService.show();
      this.loadMark();
      this.loadTitle();
    }
    this.setConfig();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  // TenantConfig取得
  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.markLabel = configs.find(_ => _.key === TenantConfig.LabelOfMark.KEY).value as string;
      this.titleLabel = configs.find(_ => _.key === TenantConfig.LabelOfTitle.KEY).value as string;
    }
  }

  loadMark() {
    this.isTrans = true;
    this.oscSpinnerService.show();
    this.markService.getMark()
      .subscribe(data => {
        this.markListDataSource.data = data.items;
      },
      error => {
        this.isTrans = false;
        this.oscSpinnerService.hide();
        console.log(error);
      },
      () => {
        this.isTrans = false;
        this.oscSpinnerService.hide();
      });
  }

  loadTitle() {
    this.isTrans = true;
    this.oscSpinnerService.show();
    this.titleService.getTitles()
      .subscribe(data => {
        this.titleListDataSource.data = data.items;
      },
      error => {
        this.isTrans = false;
        this.oscSpinnerService.hide();
        console.log(error);
      },
      () => {
        this.isTrans = false;
        this.oscSpinnerService.hide();
      });
  }

  openEditMarksDialog() {
    const dialogRef = this.dialog.open(SettingsMarksEditDialogComponent, {
      width: '800px',
      maxHeight: '95%',
      autoFocus: false,
      disableClose: true,
      data: { displayedMarkColumns: this.displayedMarkColumns }
    });

    const elements = document.getElementsByClassName('cdk-overlay-pane');
    Array.from(elements).forEach((item: HTMLElement) => {
      item.style.overflowY = 'auto';
      item.style.overflowX = 'hidden';
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === undefined || result === null) {
        dialogRef.close(SettingsMarksEditDialogComponent);
      } else {
        this.loadMark();
      }
    });
  }

  openEditTitlesDialog() {
    const dialogRef = this.dialog.open(SettingsTitlesEditDialogComponent, {
      width: '800px',
      minHeight: '630px',
      autoFocus: false,
      disableClose: true,
      data: { displayedTitleColumns: this.displayedTitleColumns }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === undefined || result === null) {
        dialogRef.close(SettingsTitlesEditDialogComponent);
      } else {
        this.loadTitle();
      }
    });
  }
}
